"use client"

import { CLOUDINARY_CLOUD } from "@/lib/cloudinary";
import { useEffect, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Menu } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

export function BlueMenu({ logoUrl: logoUrlProp, logoClassName, navClassName, backgroundColor = '#193144' }: { logoUrl?: string; logoClassName?: string; navClassName?: string; backgroundColor?: string } = {}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  // El Sheet genera un id propio para `aria-controls` que en algunos navegadores no
  // coincidía con el del servidor (aviso de hidratación). Cerrado no aporta nada al
  // HTML inicial, así que hasta montarse se pinta solo el botón, con el mismo aspecto.
  const [montado, setMontado] = useState(false)
  useEffect(() => setMontado(true), [])
  const logoUrl = logoUrlProp ?? `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/person/white_logo_transparent_cropped_btsmlv.png`

  const navItems = [
    { title: "Propiedades", href: "/propiedades/venta" },
    { title: "Vende", href: "/propietarios" },
    { title: "PersonUp", href: "/personup" },
    { title: "Universo Person", href: "/universo-person" },
    { title: "Join us", href: "/empleo" },
    { title: "Magazine", href: "/blog" },
    { title: "Contacto", href: "/contacto" },
  ]

  return (
    <nav 
      className={navClassName ?? "px-4 sm:px-6 md:px-12 lg:px-32 py-4 relative"}
      style={{ backgroundColor, zIndex: 10001 }}
    >
      <div className="container mx-auto flex items-center justify-between">
        {/* Left side: Logo and navigation items */}
        <div className="flex items-center gap-4 md:gap-8">
          <Link href="/" className="flex-shrink-0">
            <Image
              src={logoUrl}
              alt="Person Logo"
              width={96}
              height={32}
              className={logoClassName ?? "object-contain object-left h-8 sm:h-10 w-auto"}
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.title}
                href={item.href}
                className="text-white hover:text-white/80 transition-colors text-sm"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>

        {/* Right side: Mobile Menu & CTA */}
        <div className="flex items-center gap-4">
          {/* Desktop CTA - hidden on mobile */}
          <Link
            href="/valora-tu-vivienda"
            className="z-10 hidden lg:block text-white hover:text-white/80 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            Valora tu vivienda
          </Link>

          {/* Mobile Menu */}
          {!montado ? (
            <button
              className="lg:hidden text-white hover:text-white/80 transition-colors p-2 cursor-pointer"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          ) : (
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button
                className="text-white hover:text-white/80 transition-colors p-2 cursor-pointer"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] p-6 [&>button]:text-white [&>button]:hover:text-white/80" style={{ backgroundColor, zIndex: 10001 }}>
              <SheetHeader className="pb-6">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <Link href="/" onClick={() => setIsMobileMenuOpen(false)} className="flex-shrink-0">
                  <Image
                    src={logoUrl}
                    alt="Person Logo"
                    width={96}
                    height={32}
                    className="object-contain object-left h-10 w-auto"
                  />
                </Link>
              </SheetHeader>
              <nav className="flex flex-col gap-4">
                {navItems.map((item, index) => (
                  <Link
                    key={index}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-white hover:text-white/80 transition-colors py-2 text-base"
                  >
                    {item.title}
                  </Link>
                ))}
                <Link
                  href="/valora-tu-vivienda"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white hover:text-white/80 transition-colors py-2 text-base border-t border-white/20 pt-4 mt-2 cursor-pointer"
                >
                  Valora tu vivienda
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
          )}
        </div>
      </div>
    </nav>
  )
}

